"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import TicketIcon from "@heroicons/react/24/solid/TicketIcon";
import { Button } from "@/components/ui/button";
import { useSite } from "@/lib/hooks/site";
import { useVenueCompanyMaxDiscount } from "@/hooks/useVenueCompanyMaxDiscount";
import { cn } from "@/lib/utils";

const CompanyDiscountsModal = dynamic(
  () => import("@/components/views/company-discounts/CompanyDiscountsModal"),
);

interface Props {
  venueId: number;
  className?: string;
  // The narrow desktop venue sidebar has no room for the icon next to the label
  showIcon?: boolean;
}

/**
 * Shown above the WhatsApp contact button when the site has company discounts
 * enabled and the venue offers at least one. Opens the venue's company list;
 * picking a company there opens its discount card.
 */
export default function CompanyDiscountLinkButton({
  venueId,
  className,
  showIcon = true,
}: Props) {
  const site = useSite();
  const siteHasCompanyDiscounts = site?.setting?.disabledPages?.company !== 1;
  const maxDiscount = useVenueCompanyMaxDiscount(venueId, siteHasCompanyDiscounts);
  const [isOpen, setIsOpen] = useState(false);

  if (!siteHasCompanyDiscounts || !maxDiscount) return null;

  return (
    <>
      {isOpen && (
        <CompanyDiscountsModal
          id={venueId}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}

      <Button
        type="button"
        variant="primary-dark"
        onClick={() => setIsOpen(true)}
        className={cn(
          "rounded-full justify-center px-[21px] py-3 w-full text-center relative",
          className,
        )}
      >
        {showIcon && <TicketIcon className="size-4 absolute left-4" />}
        <span className={cn(showIcon && "ml-2")}>
          %{maxDiscount} Şirket İndiriminiz için Tıklayın
        </span>
      </Button>
    </>
  );
}
